import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {usePermissions} from "tmwx-design-common";
import {useEffect, useMemo, useState} from "react";
import {Button, Grid, Header, Modal, Popup, styles, Surface, Table, Text, useTheme} from '../../tmwx-design-web/src';
import {useNavigate, useSearchParams} from "react-router-dom";
import {isEmpty} from "gui-common/appHelper";
import {RootState} from "gui-common/redux/store";
import {ColumnDef} from "@tanstack/react-table";
import {ResponseBase} from "gui-common/redux/data";
import {setSnackbarData} from "gui-common/redux/action/snackbarAction";
import {
    addNewMetrics,
    clearMetricsError,
    clearMetricsResponse,
    getMetricsList
} from "gui-common/redux/action/efe/metricsAction";
import {
    AddMetricsRequest,
    initialAddMetricsRequest,
    isAddMetricsRequestValid,
    Metrics
} from "gui-common/redux/data/metrics";
import MetricsSend from "../../components/efe/MetricsSend";
import {Script} from "gui-common/redux/data/scripts";

const MetricsScreen = () => {
    const {t} = useTranslation()
    const dispatch: any = useDispatch();
    const {padding} = useTheme()
    const {hasRights} = usePermissions()
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isLoading = useSelector<RootState, boolean>(state => state.metrics.isLoading);
    const metricsList = useSelector<RootState, Metrics[]>(state => state.metrics.metricsList)
    const response = useSelector<RootState, ResponseBase | null>(state => state.metrics.response)
    const serverError = useSelector<RootState, ResponseBase | null>(state => state.metrics.error)
    const [request, setRequest] = useState<AddMetricsRequest>(initialAddMetricsRequest)
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [showDialog, setShowDialog] = useState<boolean>(false)
    const [selectedMetric, setSelectedMetric] = useState<Metrics | null>(null)
    const systemId = searchParams.get("systemId") as string;
    const podId = searchParams.get("podId") as string;
    const subtitle = [podId, systemId].filter(value => !isEmpty(value)).join(" - ");

    const handleGetList = () => {
        if (!isEmpty(systemId) && hasRights(["efe_metrics_list"])) {
            dispatch(getMetricsList(systemId))
        }
    }

    const handleClearRequest = () => {
        setValidationMessage('')
        dispatch(clearMetricsError())
        dispatch(clearMetricsResponse())
        setRequest(initialAddMetricsRequest)
    }

    const handleShow = (metric: any) => {
        setSelectedMetric(metric)
        setShowDialog(true)
    }

    const closeDialog = () => {
        setShowDialog(false)
        setSelectedMetric(null)
    }

    const handleAdd = () => {
        setShowDialog(true)
        setSelectedMetric(null)
    }

    useEffect(() => {
        if (!isEmpty(systemId) && hasRights(["efe_metrics_list"])) {
            dispatch(getMetricsList(systemId))
            setRequest({...request, systemId: systemId})
        }
    }, [searchParams, hasRights, systemId])

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClearRequest()
                handleGetList()
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))
            }
        }
    }, [response])

    const columns = useMemo<ColumnDef<Metrics>[]>(
        () => [
            {
                accessorKey: 'metricVersion',
                meta: t('atr.opParams.paramVersion'),
                maxSize: 90,
                header: () => <Text bold>{t('atr.opParams.paramVersion')}</Text>
            },
            {
                accessorKey: 'name',
                meta: t('name'),
                header: () => <Text bold>{t('name')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.name}
                    </Text>
                )
            },
            {
                accessorKey: 'metricJson',
                meta: t('efe.metricsText'),
                header: () => <Text bold>{t('efe.metricsText')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.metricJson.length > 60
                            ? row.original.metricJson.substring(0, 60) + '...'
                            : row.original.metricJson}
                    </Text>
                )
            },
            {
                accessorKey: 'requestTimestamp',
                meta: t('atr.opParams.requestTime'),
                header: () => <Text bold>{t('atr.opParams.requestTime')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.requestTimestamp ? row.original.requestTimestamp.split('.')[0].replaceAll('T', ' ') : ''}
                    </Text>
                )
            },
            {
                accessorKey: 'result',
                meta: t('result'),
                header: () => <Text bold>{t('result')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.result ?? ''}
                    </Text>
                )
            },
            {
                accessorKey: 'downloadTimestamp',
                meta: t('efe.downloadTime'),
                header: () => <Text bold>{t('efe.downloadTime')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.downloadTimestamp ? row.original.downloadTimestamp.split('.')[0].replaceAll('T', ' ') : ''}
                    </Text>
                )
            },
            {
                header: () => <Text bold centered>{t('actions')}</Text>,
                id: 'actions',
                meta: t('actions'),
                type: 'actions',
                size: 80,
                cell: ({ row }) => (
                    <div style={{ ...styles.row }}>
                        <Popup
                            style={styles.marginRight4}
                            triggerComponent={
                                <Button
                                    iconLeft={'jump_to_element_outlined'}
                                    status='primary'
                                    size='small'
                                    onPress={() => handleShow(row.original)}
                                />
                            }
                            type='hover'
                        >
                            <Surface style={{ padding: padding }} elevation={1}>
                                <Text>{t('details')}</Text>
                            </Surface>
                        </Popup>

                        <Popup
                            style={styles.marginRight4}
                            triggerComponent={
                                <Button
                                    iconLeft={'content_copy'}
                                    status='info'
                                    size='small'
                                    onPress={() => {
                                        navigator.clipboard.writeText(row.original.metricJson)
                                        dispatch(setSnackbarData({
                                            status: 'success',
                                            text: t('copyToClipboard')
                                        }))
                                    }}
                                />
                            }
                            type='hover'
                        >
                            <Surface style={{ padding: padding }} elevation={1}>
                                <Text>{t('copy')}</Text>
                            </Surface>
                        </Popup>
                    </div>
                )
            }
        ],
        [dispatch, hasRights, padding, t]
    );

    return (
        <Surface style={{height: '100vh'}} elevation={3}>
            <Header
                title={t('efe.metrics')}
                subtitle={subtitle}
                rounded
                actionLeft={<Button iconLeft={'arrow_back'} status='neutral' variant='ghost' onPress={() => {
                    handleClearRequest()
                    navigate(-1)
                }}/>}
            />
            <Grid.Container maxWidth='xl' centered>
                <Grid.Col m={12} lg={12} xl={12}>
                    <Surface rounded>
                        <Table
                            columns={columns}
                            data={metricsList}
                            loading={isLoading}
                            refreshData={() => handleGetList()}
                            dense
                            useGlobalFilter
                            add={hasRights(['efe_metrics_create']) ? () => handleAdd() : undefined}
                            labels={{
                                add: t('add'),
                                deletePreferences: t('deletePreferences'),
                                jumpHere: t('jumpHere'),
                                page: t('page'),
                                refresh: t('refresh'),
                                row: t('row'),
                                rowsSelected: t('rowsSelected'),
                                savePreferencies: t('savePreferences'),
                                searchData: t('searchData'),
                                selectCurrentPageData: t('selectCurrentPageData'),
                                showAll: t('showAll'),
                                sizing: t('sizing')
                            }}
                        />
                    </Surface>
                </Grid.Col>

                {/*{hasCreateRights && (*/}
                {/*    <Grid.Col m={4} lg={4} xl={4}>*/}
                {/*        <MetricsSend />*/}
                {/*    </Grid.Col>*/}
                {/*)}*/}
            </Grid.Container>

            <Modal
                visible={showDialog}
                type='dialog'
                dialogMaxWidth='m'
                contentElevation={0}
                closeModal={closeDialog}
                header={(
                    <Header
                        title={t('efe.metrics')}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost' onPress={closeDialog} />}
                    />
                )}
            >
                {selectedMetric ?
                    <Surface style={{ padding: padding }}>
                        <Text style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {selectedMetric?.metricJson}
                        </Text>
                    </Surface>
                    :
                    <MetricsSend onClose={closeDialog}/>
                }

            </Modal>
        </Surface>
    )
}
export default MetricsScreen;
